import React, { useState, useEffect, useCallback } from "react";
import { Users, ArrowLeft, Search, Shield, UserCheck } from "lucide-react";
import {
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL;

export function UserSwitcher({ currentEmail, isAdmin, isSuperAdmin, onSwitch }) {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState({});
  const [mode, setMode] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const origToken = localStorage.getItem("original_admin_token");
    setIsImpersonating(!!origToken);
  }, []);

  // Accessible si admin OU si en mode impersonation (l'admin original garde le contrôle)
  const canSwitchUsers = isAdmin || isSuperAdmin || isImpersonating;

  const fetchUsers = useCallback(async () => {
    if (!canSwitchUsers) return;
    setLoading(true);
    try {
      // En impersonation, utiliser le token admin original pour lister les users
      const origToken = localStorage.getItem("original_admin_token");
      const token = origToken || localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/hub/auth/tenant-users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": localStorage.getItem("active_tenant") || "tef",
        },
      });
      if (res.ok) {
        const data = await res.json();
        setMode(data.mode);
        if (data.mode === "super_admin") {
          setGroups(data.groups || {});
        } else {
          setUsers(data.users || []);
        }
      }
    } catch (e) {
      console.error("Failed to fetch users for switcher:", e);
    } finally {
      setLoading(false);
    }
  }, [canSwitchUsers]);

  const handleImpersonate = async (email) => {
    try {
      // Toujours utiliser le token admin original pour l'impersonation
      const origToken = localStorage.getItem("original_admin_token");
      const adminToken = origToken || localStorage.getItem("session_token");
      
      const res = await fetch(`${API}/api/hub/auth/impersonate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
          "X-Tenant-ID": localStorage.getItem("active_tenant") || "tef",
        },
        body: JSON.stringify({ target_email: email }),
      });
      if (res.ok) {
        const data = await res.json();
        // Sauvegarder le token admin original (le tout premier)
        if (!origToken) {
          localStorage.setItem("original_admin_token", adminToken);
        }
        localStorage.setItem("session_token", data.session_token);
        onSwitch?.(data);
        window.location.reload();
      }
    } catch (e) {
      console.error("Impersonation failed:", e);
    }
  };

  const handleSwitchBack = async () => {
    const origToken = localStorage.getItem("original_admin_token");
    if (!origToken) return;
    
    // Restaurer directement le token admin original
    localStorage.setItem("session_token", origToken);
    localStorage.removeItem("original_admin_token");
    window.location.reload();
  };

  if (!canSwitchUsers) return null;

  const filterUsers = (list) => {
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter(
      (u) =>
        u.name?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s)
    );
  };

  const roleIcon = (rôle) => {
    if (rôle === "admin" || rôle === "super_admin")
      return <Shield className="w-3 h-3 text-[var(--oh-info-text)]" />;
    return <UserCheck className="w-3 h-3 text-[var(--oh-text-muted)]" />;
  };

  const renderUserItem = (u) => {
    const isCurrent = u.email === currentEmail;
    return (
      <DropdownMenuItem
        key={u.email}
        onClick={() => !isCurrent && handleImpersonate(u.email)}
        className={`cursor-pointer text-xs gap-2 ${
          isCurrent
            ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]"
            : "hover:bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)]"
        }`}
        data-testid={`switch-user-${u.email}`}
      >
        <div className="w-5 h-5 rounded-full bg-[var(--oh-bg-elevated)] flex items-center justify-center text-[10px] font-medium shrink-0">
          {u.name?.charAt(0) || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate font-medium">{u.name || u.email}</p>
          <p className="truncate text-[var(--oh-text-muted)] text-[10px]">{u.email}</p>
        </div>
        {roleIcon(u.rôle)}
        {isCurrent && (
          <span className="text-[9px] text-[var(--oh-accent-text)] font-medium">ACTUEL</span>
        )}
      </DropdownMenuItem>
    );
  };

  return (
    <>
      {isImpersonating && (
        <DropdownMenuItem
          onClick={handleSwitchBack}
          className="cursor-pointer text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)] text-xs font-medium gap-2"
          data-testid="switch-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Revenir Admin
        </DropdownMenuItem>
      )}
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          className="cursor-pointer hover:bg-[var(--oh-bg-elevated)] text-xs gap-2"
          data-testid="switch-user-trigger"
          onPointerEnter={fetchUsers}
          onClick={fetchUsers}
        >
          <Users className="w-4 h-4 mr-1 text-[var(--oh-accent-text)]" />
          Changer d&apos;utilisateur
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent
          className="bg-[var(--oh-bg-surface)] border-white/10 w-72 max-h-80 overflow-y-auto"
          data-testid="switch-user-panel"
        >
          <div className="p-2 sticky top-0 bg-[var(--oh-bg-surface)] z-10 border-b border-white/5">
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[var(--oh-text-muted)]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-7 pr-2 py-1.5 text-xs bg-[var(--oh-bg-elevated)] border border-white/10 rounded text-[var(--oh-text)] placeholder:text-[var(--oh-text-muted)] focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                data-testid="switch-user-search"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {loading ? (
            <div className="p-4 text-center text-xs text-[var(--oh-text-muted)]">
              Chargement...
            </div>
          ) : mode === "super_admin" ? (
            Object.entries(groups).map(([tenant, tenantUsers]) => (
              <div key={tenant}>
                <div className="px-3 py-1.5 text-[10px] font-medium text-[var(--oh-text-secondary)] uppercase tracking-wider bg-[var(--oh-bg-elevated)] sticky top-10">
                  {tenant}
                </div>
                {filterUsers(tenantUsers).map(renderUserItem)}
              </div>
            ))
          ) : (
            filterUsers(users).map(renderUserItem)
          )}

          {!loading &&
            mode !== "super_admin" &&
            filterUsers(users).length === 0 && (
              <div className="p-3 text-center text-xs text-[var(--oh-text-muted)]">
                Aucun utilisateur trouvé
              </div>
            )}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}
